/**
 * Cinematic Studio — scene document model.
 *
 * A scene is a self-contained promo stage: aircraft placed freely (nothing to do
 * with real allocations), optional motion paths, camera tours, and look
 * settings. Scenes live in localStorage and export/import as a single JSON file.
 *
 * Coordinate conventions (matching the rest of the 3D layer):
 *  - positions are **scene feet** `[x, z]`, relative to `meta.origin`;
 *    +X = east, +Z = south.
 *  - `headingDeg` is **compass** — 0 = north, clockwise. The renderer wants
 *    scene Y-rotation instead, so it is converted at the boundary via
 *    `engine/heading.ts`. Storing compass keeps the JSON human-editable.
 *  - altitudes are feet above ground.
 *
 * Because positions are origin-relative, a scene authored at one airfield still
 * loads and lays out correctly at another — it just sits over different pavement.
 */
import type { LightPreset, MapStyleId } from '@/stores/mapStore'
import type { FlythroughPlaylist } from '@/features/flythrough'

/** Bumped only for changes the migration chain must know about. */
export const CINEMATIC_SCENE_VERSION = 1

export type CinematicRenderMode = 'solid' | 'ghost_away' | 'ghost_incoming' | 'ghost_returning'

export type PathPlayMode = 'once' | 'loop' | 'pingPong'

/** Keys into EASES in `demo/masters-planner/motion/easing.ts`. */
export type PathEasing = 'linear' | 'inOut' | 'in' | 'out'

export interface CinematicPathWaypoint {
  id: string
  /** Scene feet [x, z]. */
  pos: [number, number]
  altitudeFt: number
  /** Sit motionless here before continuing, ms. */
  holdMs: number
}

export interface CinematicPath {
  waypoints: CinematicPathWaypoint[]
  /**
   * 'tangent' is autorotation — the aircraft's nose follows the direction of
   * travel. 'fixed' holds `fixedHeadingDeg` (or the actor's own heading).
   */
  headingMode: 'tangent' | 'fixed'
  fixedHeadingDeg: number | null
  /** Delay before motion starts, ms from scene t=0. */
  startOffsetMs: number
  /** Time for ONE traversal, ms. */
  durationMs: number
  playMode: PathPlayMode
  easing: PathEasing
  /** Round the corners with a Catmull-Rom pass. */
  smooth: boolean
}

export interface CinematicAircraft {
  id: string
  /** Display name in the actor list; also the tail number shown in-scene. */
  name: string
  /** Key into the aircraft_types catalog. */
  typeCode: string
  /** Scene feet [x, z] — the resting pose when `path` is null. */
  position: [number, number]
  /** Compass heading, 0 = north. */
  headingDeg: number
  altitudeFt: number
  scale: number
  renderMode: CinematicRenderMode
  path: CinematicPath | null
}

export interface CinematicSceneSettings {
  mapStyleId: MapStyleId
  lightPreset: LightPreset
  showBuildings: boolean
  showRoofs: boolean
  /** Master scene clock length, ms. */
  durationMs: number
  loopScene: boolean
}

export interface CinematicSceneMeta {
  id: string
  name: string
  /** `CINEMATIC_SCENE_VERSION` at write time. */
  schemaVersion: number
  locationId: string | null
  locationSlug: string | null
  /** Scene origin [lng, lat] — every position above is relative to this. */
  origin: [number, number] | null
  createdAt: string
  updatedAt: string
}

export interface CinematicScene {
  meta: CinematicSceneMeta
  settings: CinematicSceneSettings
  aircraft: CinematicAircraft[]
  /** Camera tours, in the flythrough module's own format. */
  playlists: FlythroughPlaylist[]
}

/** What lands in the downloaded `.json`. */
export interface CinematicSceneFile {
  kind: 'planmax.cinematic.scene'
  version: number
  scene: CinematicScene
}

// ── Defaults ────────────────────────────────────────────────────────────────

export const DEFAULT_SCENE_DURATION_MS = 60_000

export const DEFAULT_SETTINGS: CinematicSceneSettings = {
  mapStyleId: 'satellite-streets',
  lightPreset: 'day',
  showBuildings: true,
  showRoofs: true,
  durationMs: DEFAULT_SCENE_DURATION_MS,
  loopScene: true,
}

export const DEFAULT_PATH: Omit<CinematicPath, 'waypoints'> = {
  headingMode: 'tangent',
  fixedHeadingDeg: null,
  startOffsetMs: 0,
  durationMs: 20_000,
  playMode: 'loop',
  easing: 'inOut',
  smooth: true,
}

/** Scene object id for a studio actor — namespaced so it can never collide. */
export const sceneIdFor = (actorId: string) => `cine:${actorId}`
